import { TypedDocumentNode, gql } from '@apollo/client';
import { Suspense } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { useChaseLayoutData } from 'pages/play/pro/_$fixture/chase/layout';

import {
  BaseballPlayProFixtureChaseUserGroupLayoutQuery,
  BaseballPlayProFixtureChaseUserGroupLayoutQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const query = gql`
  query BaseballPlayProFixtureChaseUserGroupLayoutQuery(
    $userGroupSlug: String!
  ) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        rarityType
        displayName
        topScoresCount
        startGameWeek
        endGameWeek
      }
    }
  }
` as TypedDocumentNode<
  BaseballPlayProFixtureChaseUserGroupLayoutQuery,
  BaseballPlayProFixtureChaseUserGroupLayoutQueryVariables
>;

type LayoutData = {
  so5UserGroup: NonNullable<
    BaseballPlayProFixtureChaseUserGroupLayoutQuery['so5']['so5UserGroup']
  >;
  chaseLeaderboards: ReturnType<typeof useChaseLayoutData>['chaseLeaderboards'];
  layoutLoading: boolean;
};

export const useChaseUserGroupLayoutData = () => {
  return useOutletContext<LayoutData>();
};

const PlayProFixtureChaseUserGroupLayout = () => {
  const { chaseLeaderboards } = useChaseLayoutData();
  const { userGroup: userGroupSlug } = useTypedParams<LayoutParams>();

  const { data, loading: layoutLoading } = useQuery(query, {
    variables: {
      userGroupSlug,
    },
    skip: chaseLeaderboards.length === 0,
  });

  const so5UserGroup = data?.so5?.so5UserGroup;

  return (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <Outlet context={{ chaseLeaderboards, so5UserGroup, layoutLoading }} />
    </Suspense>
  );
};

export default PlayProFixtureChaseUserGroupLayout;
